/**
 * IncidentDetail — admin view of an incident report (read-only audit + body map).
 */
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, FileDown } from "lucide-react";
import { api, API } from "../../lib/api";
import { useI18n } from "../../lib/i18n";
import BodyMap from "../../components/BodyMap";

const SEVERITY_PALETTE = {
  low: "text-[#6B7280]",
  medium: "text-[#8C6D1F]",
  high: "text-[#A11D1D]",
  critical: "text-[#A11D1D] font-semibold",
};
const STATUS_PALETTE = {
  draft: "bg-[#EEF0F4] text-[#6B7280]",
  pending_ack: "bg-[#FDF1D2] text-[#8C6D1F]",
  acknowledged: "bg-[#E6EFE8] text-[#1F3A2A]",
  closed: "bg-[#EEF0F4] text-[#9CA3AF]",
};

export default function IncidentDetail() {
  const { t } = useI18n();
  const { id } = useParams();
  const [inc, setInc] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/admin/ops/incidents/${id}`);
        setInc(r.data);
      } catch {
        // ignore
      }
      setLoading(false);
    })();
  }, [id]);

  const downloadPdf = async () => {
    try {
      const r = await api.get(`/admin/ops/incidents/${id}/pdf`, { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${inc?.incident_number || id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      window.open(`${API}/admin/ops/incidents/${id}/pdf`, "_blank");
    }
  };

  if (loading) {
    return (
      <main className="p-6 lg:p-10">
        <p className="font-serif italic text-[#525252]">{t("ops.common.loading")}</p>
      </main>
    );
  }
  if (!inc) return null;

  return (
    <main className="p-6 lg:p-10 max-w-4xl mx-auto space-y-6" data-testid="admin-incident-detail">
      <Link
        to="/admin/ops/incidents"
        className="inline-flex items-center gap-2 text-sm text-[#6B7280] hover:text-[#1A1B2E]"
        data-testid="incident-detail-back"
      >
        <ArrowLeft size={14} strokeWidth={1.6} />
        {t("ops.incidents.detail_back")}
      </Link>

      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <span className="eyebrow">{t("ops.incidents.detail_incident")}</span>
          <h1 className="mt-2 font-display text-3xl text-[#1A1B2E]" data-testid="incident-detail-number">
            {inc.incident_number}
          </h1>
          <p className="mt-1 text-sm text-[#6B7280]">
            {inc.child_name} · {inc.room_name || "—"} · {inc.occurred_at?.slice(0, 16).replace("T", " ")}
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <span className={`inline-block text-[10px] tracking-[0.1em] uppercase font-semibold rounded-full px-2.5 py-0.5 ${STATUS_PALETTE[inc.status]}`}>
            {t(`ops.incidents.status_${inc.status}`)}
          </span>
          <span className={`text-sm ${SEVERITY_PALETTE[inc.severity]}`}>
            {t(`ops.incidents.severity_${inc.severity}`)}
          </span>
          <button
            onClick={downloadPdf}
            data-testid="incident-detail-download-pdf"
            className="inline-flex items-center gap-2 text-sm text-[#1F3A2A] border border-[#EEF0F4] rounded-full px-3 py-1.5 hover:bg-[#FAFAFB]"
          >
            <FileDown size={14} strokeWidth={1.6} />
            {t("ops.incidents.download_pdf")}
          </button>
        </div>
      </header>

      <section className="rounded-3xl bg-white border border-[#EEF0F4] p-6">
        <h3 className="font-display text-lg text-[#1A1B2E]">{t("ops.incidents.detail_summary")}</h3>
        <p className="mt-2 text-sm text-[#1A1B2E] leading-relaxed">{inc.description}</p>
        {inc.location_text && (
          <p className="mt-2 text-xs text-[#6B7280]">Location: {inc.location_text}</p>
        )}
      </section>

      <section className="rounded-3xl bg-white border border-[#EEF0F4] p-6">
        <h3 className="font-display text-lg text-[#1A1B2E]">{t("ops.incidents.form_body_map")}</h3>
        <div className="mt-3">
          <BodyMap selected={inc.body_map_locations || []} readOnly />
        </div>
      </section>

      <section className="rounded-3xl bg-white border border-[#EEF0F4] p-6">
        <h3 className="font-display text-lg text-[#1A1B2E]">{t("ops.incidents.detail_action")}</h3>
        <p className="mt-2 text-sm text-[#1A1B2E] leading-relaxed">{inc.action_taken}</p>
      </section>

      {(inc.witnesses || []).length > 0 && (
        <section className="rounded-3xl bg-white border border-[#EEF0F4] p-6">
          <h3 className="font-display text-lg text-[#1A1B2E]">{t("ops.incidents.detail_witnesses")}</h3>
          <ul className="mt-3 space-y-2">
            {inc.witnesses.map((w, i) => (
              <li key={i} className="text-sm text-[#1A1B2E]">
                <span className="font-medium">{w.name}</span>
                {w.role ? ` · ${w.role}` : ""}
                {w.statement ? <p className="text-xs text-[#6B7280] mt-0.5">{w.statement}</p> : null}
              </li>
            ))}
          </ul>
        </section>
      )}

      <section
        className={`rounded-3xl border p-6 ${inc.parent_signature ? "bg-[#FAF9F1] border-[#EEF0F4]" : "bg-[#FDF1D2] border-[#F4E5C5]"}`}
        data-testid="incident-detail-signature"
      >
        <h3 className="font-display text-lg text-[#1A1B2E]">
          {t("ops.incidents.detail_signature")}
        </h3>
        {inc.parent_signature ? (
          <div className="mt-2 text-sm text-[#1A1B2E]">
            <div>
              {t("ops.incidents.detail_signed_by")}{" "}
              <span className="font-medium">{inc.parent_signature.signer_name}</span>
            </div>
            <div className="text-xs text-[#6B7280] mt-0.5">
              {t("ops.incidents.detail_signed_at")}{" "}
              {inc.parent_signature.signed_at?.slice(0, 16).replace("T", " ")}
            </div>
            <p className="mt-3 font-serif italic text-[#1F3A2A]">
              "{inc.parent_signature.typed_name}"
            </p>
          </div>
        ) : (
          <p className="mt-2 font-serif italic text-[#8C4A1F]">
            {t("ops.incidents.detail_no_signature")}
          </p>
        )}
      </section>

      <section className="rounded-3xl bg-white border border-[#EEF0F4] p-6">
        <h3 className="font-display text-lg text-[#1A1B2E]">{t("ops.incidents.detail_audit_trail")}</h3>
        <ul className="mt-3 space-y-2 text-sm text-[#6B7280]">
          {(inc.audit_trail || []).map((a, i) => (
            <li key={i} className="text-xs">
              <span className="text-[#1A1B2E] font-medium">{a.actor}</span> · {a.action} ·{" "}
              {a.at?.slice(0, 16).replace("T", " ")}
            </li>
          ))}
        </ul>
      </section>
    </main>
  );
}
